import React, { useState, useContext, memo } from 'react';
import {
  FeatureContext,
  useSessionStorage,
} from '@monash/portal-frontend-common';
import PageContainer from '../PageContainer';
import { DataContext } from '@/components/providers/data-provider/DataProvider';
import UpcomingEventDetail from './components/UpcomingEventDetail';
import UpcomingListingDesktop from './components/UpcomingListingDesktop';
import UpcomingListingMobile from './components/UpcomingListingMobile';
import { differenceInMonths } from 'date-fns';
import { useInterval } from '@/hooks/use-interval';
import useLiveDate from '@/hooks/use-live-date';
import LoadingWrapper from '@/components/ui/loading-wrapper/LoadingWrapper';
import { eventsOnDay } from './utils/utils';
import { pageIds } from '../../../constants';
import { useResponsiveValue } from '@monash/portal-react';
import { MOBILE_RESPONSIVE } from '@/components/ui/main/Main';
import { getHighestCourseStatus } from './utils/get-highest-course-status';
import { getMessage } from './utils/get-message';
import c from './upcoming.module.scss';

export const Upcoming = memo(({ pagesData, globalBannerShownPageId }) => {
  const [forceCodeError] = useSessionStorage('forceCodeError');

  if (forceCodeError) {
    const error = {};
    alert(error.throws.forcedCodeErrorIsOn);
  }

  const {
    userCourses,
    upcomingEvents,
    userEvents,
    loading,
    userEventsError,
    academicResults,
    gradeScaleTypes,
  } = useContext(DataContext);

  const { get } = useLiveDate();
  const [currentDate, setCurrentDate] = useState(get());
  const { UPCOMING } = pageIds;

  useInterval(() => {
    setCurrentDate(get());
  }, 5000);

  const [activeIndex, setActiveIndex] = useState(0);
  const [showLocation, setShowLocation] = useSessionStorage(
    'showLocation',
    true
  );
  const responsiveSize = useResponsiveValue(MOBILE_RESPONSIVE);

  const numMonthsApart = differenceInMonths(
    upcomingEvents?.[0]?.start?.time,
    currentDate
  );

  const pageBannerShown = pagesData?.pageBanners?.[UPCOMING];
  const globalBannerShown = globalBannerShownPageId === UPCOMING;
  const bannerShown = pageBannerShown || globalBannerShown;

  const courseStatus = getHighestCourseStatus(userCourses);
  const message = getMessage({
    academicResults,
    courseStatus,
    userCourses,
    upcomingEvents,
    numMonthsApart,
    bannerShown: bannerShown ? UPCOMING : null,
  });

  if (!loading.userEvents && message) {
    return message;
  }

  // Upcoming events and active event details
  // if there are no events on today, then we have to adjust the given active index by -1 to correctly index the upcomingEvents array
  // b/c there is an additional event card that displays "No events" on the upcoming listing component
  // i.e. logically, the events data would be akin to something like:
  // upcoming listing: [no events card, event card 1, event card 2]
  // upcoming events: [event card 1, event card 2]
  const eventsOnToday = eventsOnDay(upcomingEvents, currentDate);
  const adjustedActiveIndex = eventsOnToday ? activeIndex : activeIndex - 1;
  const activeEvent = upcomingEvents[adjustedActiveIndex];
  const nextEvent = upcomingEvents[0];
  const loadingUserEvents = loading.userEvents;

  const { featureFlags } = useContext(FeatureContext);

  return (
    <>
      <div
        className={c.Today}
        data-banner-shown={bannerShown ? UPCOMING : null}
      >
        <PageContainer
          type="upcoming"
          pageId={UPCOMING}
          loading={loadingUserEvents}
        >
          {loadingUserEvents ? (
            <LoadingWrapper />
          ) : (
            <div
              className={c.wrapper}
              aria-label="Upcoming Events board"
              role="region"
            >
              {responsiveSize === 'L' ? (
                <>
                  <UpcomingListingDesktop
                    currentDate={currentDate}
                    events={upcomingEvents}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                    showLocation={showLocation}
                    setShowLocation={setShowLocation}
                    userEventsError={userEventsError}
                    featureFlags={featureFlags}
                  />

                  <UpcomingEventDetail
                    currentDate={currentDate}
                    event={activeEvent}
                    nextEvent={nextEvent}
                    allEvents={userEvents}
                    activeIndex={activeIndex}
                    gradeScaleTypes={gradeScaleTypes}
                    featureFlags={featureFlags}
                  />
                </>
              ) : (
                <UpcomingListingMobile
                  currentDate={currentDate}
                  events={upcomingEvents}
                  allEvents={userEvents}
                  userEventsError={userEventsError}
                  gradeScaleTypes={gradeScaleTypes}
                  featureFlags={featureFlags}
                />
              )}
            </div>
          )}
        </PageContainer>
      </div>
    </>
  );
});
